import React from 'react';
import { resolveActiveSearcherId, visibleSearchers } from 'shared/searchers';
import type { HeaderConfig } from './types';
import type { HeroConfig } from '../hero/types';
import HeroSearchCard from '../hero/HeroSearchCard';
import type { HeaderVariant } from './types';

interface ReserveSearchPanelProps {
  variant: Extract<HeaderVariant, 'reserve' | 'ecommerce'>;
  open: boolean;
  onClose: () => void;
  headerConfig: HeaderConfig;
  heroSearchConfig?: Partial<HeroConfig>;
  onSearcherSelect?: (searcherId: string) => void;
  renderSearchForm?: (activeSearcherId: string) => React.ReactNode;
  showPanelTopRow?: boolean;
  paisId?: number;
}

const ReserveSearchPanel: React.FC<ReserveSearchPanelProps> = ({
  variant,
  open,
  onClose,
  headerConfig,
  onSearcherSelect,
  renderSearchForm,
  showPanelTopRow = true,
  paisId,
}) => {
  const searchers = visibleSearchers(headerConfig.searcherItems || []);
  const activeSearcherId = resolveActiveSearcherId(
    headerConfig.searcherItems,
    headerConfig.activeSearcherId,
  );

  if (searchers.length === 0) {
    return null;
  }

  const panelClass = [
    'header-reserve-panel',
    open && 'header-reserve-panel--open',
    variant === 'ecommerce' && 'header-reserve-panel--ecommerce',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      aria-hidden={!open}
      className={panelClass}
      id={variant === 'reserve' ? 'header-reserve-search-panel' : undefined}
    >
      {showPanelTopRow ? (
        <div className='header-reserve-panel__top'>
          <nav className='header-reserve-panel__tabs'>
            {searchers.map((searcher) => {
              const iconStyle = {
                '--searcher-icon-url': `url("${searcher.iconUrl}")`,
              } as React.CSSProperties;
              const isActive = searcher.id === activeSearcherId;

              if (searcher.urlOnly) {
                return (
                  <a
                    className='header-reserve-panel__tab header-reserve-panel__tab--link'
                    href={searcher.url || '#'}
                    key={searcher.id}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    <span className='header-reserve-panel__tab-icon' style={iconStyle} />
                    <span>{searcher.label}</span>
                  </a>
                );
              }

              return (
                <button
                  className={[
                    'header-reserve-panel__tab',
                    isActive && 'header-reserve-panel__tab--active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  key={searcher.id}
                  onClick={() => onSearcherSelect?.(searcher.id)}
                  type='button'
                >
                  <span className='header-reserve-panel__tab-icon' style={iconStyle} />
                  <span>{searcher.label}</span>
                </button>
              );
            })}
          </nav>
          <button
            aria-label='Cerrar buscador'
            className='header-reserve-panel__close'
            onClick={onClose}
            type='button'
          >
            ×
          </button>
        </div>
      ) : null}
      <div className='header-reserve-panel__card'>
        {/* Institucional / Ecommerce: no searcher title or benefits (prototipo v18). */}
        <HeroSearchCard
          activeSearcherId={activeSearcherId}
          activeSearcherLabel=''
          benefits={[]}
          paisId={paisId}
          renderSearchForm={renderSearchForm}
          showTitle={false}
          title=''
        />
      </div>
    </div>
  );
};

export default ReserveSearchPanel;
